import type { Recipe } from '../../services/recipes/recipesService';
import { ALLERGEN_SUBTYPES, getUnmappedLegacyAllergens, isSupportedAllergenDetails } from '../../types/RecipeAllergens';
import { ALLERGIES, Allergy } from '../../types/RecipeEnums';

export type AllergenState = 'contains' | 'absent' | 'unknown';

// Match the app's declaration semantics; never derive allergens from ingredients or diets.
export function allergenDeclaration(recipe: Recipe, parent: Allergy, stale = false) {
  const document = recipe.allergenDetails;
  const supported = isSupportedAllergenDetails(document);
  const group = document?.version === 1 ? document.groups?.[parent] : undefined;
  const validValues = ALLERGEN_SUBTYPES[parent];
  const rawValues = group && Array.isArray(group.subtypes) ? group.subtypes : [];
  const subtypes = validValues?.filter(value => rawValues.includes(value)) ?? [];
  const reviewed = supported && document.review.status === 'reviewed' && !stale;
  const complete = group?.subtypesComplete === true && subtypes.length > 0 &&
    rawValues.every(value => validValues?.includes(value));
  const contains = recipe.allergies?.includes(parent) || group?.presence === 'contains';
  const state: AllergenState = contains ? 'contains' : reviewed && group?.presence === 'absent' ? 'absent' : 'unknown';
  return { state, subtypes, complete, reviewed, needsReview: !reviewed, unsupported: !!document && !supported };
}

// Presentation only: missing detail is never treated as confirmed absence.
export function hasIncompleteAllergenInformation(recipe: Recipe, stale = false): boolean {
  return getUnmappedLegacyAllergens(recipe.allergies).length > 0 || ALLERGIES.some(parent => {
    const declaration = allergenDeclaration(recipe, parent, stale);
    return !declaration.reviewed || declaration.state === 'unknown' ||
      (declaration.state === 'contains' && !!ALLERGEN_SUBTYPES[parent] && !declaration.complete);
  });
}
